import type { SelfServiceData, Step, StepId, WizardPhaseId } from '../../types';
import type { WizardPhaseDefinition } from '../flowTypes';

export interface WizardProgressPhaseState extends WizardPhaseDefinition {
  status: 'complete' | 'current' | 'upcoming';
  isVisited: boolean;
}

export interface WizardProgress {
  stepIndex: number;
  stepTotal: number;
  progressPct: number;
  journey: StepId[];
  currentPhaseId: WizardPhaseId | null;
  phaseIndex: number;
  phaseProgressPct: number;
  phases: WizardProgressPhaseState[];
}

const COMPLETION_STEPS = new Set<StepId>([
  'confirmation',
  'consent_confirmation',
  'dl_confirmation',
  'dlc_confirmation',
  'bk_confirmation'
]);

const clamp = (value: number, min: number, max: number) => {
  return Math.min(max, Math.max(min, value));
};

const buildJourneyFromHistory = (
  history: StepId[],
  currentStepId: StepId
): StepId[] => {
  const raw = [...history, currentStepId].filter(Boolean) as StepId[];
  if (raw.length === 0) {
    return [];
  }

  const journey: StepId[] = [];
  const seen = new Set<StepId>();

  for (const step of raw) {
    if (seen.has(step)) {
      continue;
    }
    journey.push(step);
    seen.add(step);
  }

  return journey;
};

const buildJourneyFromState = (
  startStep: StepId,
  currentStepId: StepId,
  steps: Partial<Record<StepId, Step>>,
  selfServiceData: SelfServiceData
): StepId[] => {
  const journey: StepId[] = [];
  const visited = new Set<StepId>();
  let pointer: StepId | null = startStep;
  let safety = 0;

  while (pointer && safety < 100) {
    const currentPointer = pointer as StepId;
    const stepMeta = steps[currentPointer];

    journey.push(currentPointer);
    if (currentPointer === currentStepId) {
      return journey;
    }

    if (!stepMeta) {
      break;
    }

    visited.add(currentPointer);
    const nextStep = (stepMeta.getNext?.(selfServiceData) as StepId | null) ?? (stepMeta.next as StepId | null) ?? null;
    if (!nextStep || visited.has(nextStep)) {
      break;
    }

    pointer = nextStep;
    safety += 1;
  }

  if (!journey.includes(currentStepId) && steps[currentStepId]) {
    journey.push(currentStepId);
  }

  return journey;
};

const projectFutureJourney = (
  journey: StepId[],
  currentStepId: StepId,
  steps: Partial<Record<StepId, Step>>,
  selfServiceData: SelfServiceData
): StepId[] => {
  const projected = [...journey];
  const seen = new Set<StepId>(projected);
  let pointer = currentStepId;
  let safety = 0;

  while (safety < 50) {
    const meta = steps[pointer];
    if (!meta) {
      break;
    }

    const candidate = (meta.getNext?.(selfServiceData) as StepId | null) ?? (meta.next as StepId | null) ?? null;
    if (!candidate) {
      break;
    }
    if (seen.has(candidate)) {
      break;
    }

    projected.push(candidate);
    seen.add(candidate);
    pointer = candidate;
    safety += 1;

    if (COMPLETION_STEPS.has(candidate)) {
      break;
    }
  }

  return projected;
};

const buildPhaseStates = (
  phases: ReadonlyArray<WizardPhaseDefinition>,
  steps: Partial<Record<StepId, Step>>,
  journey: StepId[],
  currentPhaseId: WizardPhaseId | null
): { states: WizardProgressPhaseState[]; phaseIndex: number } => {
  const visitedPhaseIds = journey
    .map((stepId) => steps[stepId]?.phase)
    .filter((phaseId): phaseId is WizardPhaseId => Boolean(phaseId));

  const visitedPhaseSet = new Set<WizardPhaseId>(visitedPhaseIds);
  const currentIndex = currentPhaseId ? phases.findIndex((phase) => phase.id === currentPhaseId) : -1;
  const furthestVisitedIndex = phases.reduce((acc, phase, index) => {
    return visitedPhaseSet.has(phase.id) ? Math.max(acc, index) : acc;
  }, -1);

  const effectiveIndex = currentIndex >= 0 ? currentIndex : furthestVisitedIndex >= 0 ? furthestVisitedIndex : 0;

  const states = phases.map((phase, index) => {
    const isVisited = visitedPhaseSet.has(phase.id) || (currentPhaseId === null && index === 0);
    const status: 'complete' | 'current' | 'upcoming' = index < effectiveIndex ? 'complete' : index === effectiveIndex ? 'current' : 'upcoming';

    return {
      ...phase,
      status,
      isVisited
    };
  });

  return { states, phaseIndex: effectiveIndex };
};

const calculatePhaseProgress = (
  phases: ReadonlyArray<WizardProgressPhaseState>,
  phaseIndex: number,
  journey: StepId[],
  steps: Partial<Record<StepId, Step>>,
  currentStepId: StepId
): number => {
  if (phases.length === 0) {
    return 0;
  }

  const completedPhases = phases.filter((phase) => phase.status === 'complete').length;
  const perPhaseValue = 100 / phases.length;

  const currentPhase = phases[phaseIndex];
  if (!currentPhase) {
    return clamp(perPhaseValue * completedPhases, 0, 99);
  }

  const stepsInCurrentPhase = Object.values(steps).filter((step): step is Step => !!step && step.phase === currentPhase.id);
  const visitedInCurrentPhase = journey.filter((stepId) => steps[stepId]?.phase === currentPhase.id);
  const intraPhaseFraction = stepsInCurrentPhase.length > 0 ? Math.min(visitedInCurrentPhase.length / stepsInCurrentPhase.length, 1) : 0;

  const raw = perPhaseValue * completedPhases + perPhaseValue * intraPhaseFraction;
  const clamped = clamp(raw, 0, COMPLETION_STEPS.has(currentStepId) ? 100 : 99);
  return clamped;
};

export const calculateProgress = (
  history: StepId[],
  currentStepId: StepId,
  steps: Partial<Record<StepId, Step>>,
  phases: ReadonlyArray<WizardPhaseDefinition>,
  selfServiceData: SelfServiceData,
  startStep: StepId
): WizardProgress => {
  const journeyFromState = buildJourneyFromState(startStep, currentStepId, steps, selfServiceData);
  const journeyFromHistory = buildJourneyFromHistory(history, currentStepId);

  const journey = journeyFromState.includes(currentStepId)
    ? journeyFromState
    : journeyFromHistory.length > 0
      ? journeyFromHistory
      : [currentStepId];

  const projectedJourney = projectFutureJourney(journey, currentStepId, steps, selfServiceData);
  const stepTotal = Math.max(1, projectedJourney.length);

  const currentIndex = journey.indexOf(currentStepId);
  const stepIndex = currentIndex === -1 ? Math.max(1, journey.length || 1) : currentIndex + 1;

  const currentPhaseId = steps[currentStepId]?.phase ?? null;
  const { states: phaseStates, phaseIndex } = buildPhaseStates(phases, steps, journey, currentPhaseId);
  const phaseProgressPct = calculatePhaseProgress(phaseStates, phaseIndex, journey, steps, currentStepId);

  const progressPct = COMPLETION_STEPS.has(currentStepId) ? 100 : phaseProgressPct;

  return {
    stepIndex,
    stepTotal,
    progressPct,
    journey,
    currentPhaseId,
    phaseIndex,
    phaseProgressPct,
    phases: phaseStates
  };
};


